/**
 * ConsultingSuiteAdminPage - Admin panel for Consulting Suite configuration
 * Includes: Proposal Builder Settings, Planning Defaults, Rate Card Management,
 * Templates Pack Manager, Conversion Rules
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase, Settings, FileText, Calendar, DollarSign, 
  Download, ArrowRightCircle, Save, Check, X, Plus, Trash2,
  ChevronDown, ChevronRight, Users, Flag, Clock, AlertTriangle,
  GitBranch, Layers, RefreshCw, Upload, Eye, EyeOff, Brain, Shield
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';

const ConsultingSuiteAdminPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('proposal-settings');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // State for different sections
  const [proposalSettings, setProposalSettings] = useState({
    enabled: true,
    defaultSections: ['discovery', 'scope', 'acceptance', 'planning', 'estimation', 'proposal'],
    requireApproval: false,
    autoNumbering: true,
    proposalPrefix: 'PROP'
  });
  
  const [planningDefaults, setPlanningDefaults] = useState({
    defaultPhases: [
      { name: 'Discovery & Planning', durationWeeks: 2, deliverables: ['Requirements', 'Project Plan'] },
      { name: 'Development', durationWeeks: 6, deliverables: ['Core Features', 'Integration'] },
      { name: 'Testing & QA', durationWeeks: 2, deliverables: ['Test Reports', 'Bug Fixes'] },
      { name: 'UAT & Deployment', durationWeeks: 2, deliverables: ['User Acceptance', 'Go-Live'] }
    ],
    defaultGovernance: [
      { name: 'Daily Standup', type: 'daily' },
      { name: 'Weekly Status', type: 'weekly' },
      { name: 'Steering Committee', type: 'monthly' }
    ],
    defaultMilestones: [
      { name: 'Project Kickoff', week: 1 },
      { name: 'MVP Complete', week: 6 },
      { name: 'UAT Sign-off', week: 10 },
      { name: 'Go-Live', week: 12 }
    ],
    defaultRoles: ['Project Manager', 'Business Analyst', 'Developer', 'QA Engineer', 'UX Designer']
  });
  
  const [rateCardDefaults, setRateCardDefaults] = useState({
    defaultCurrency: 'ZAR',
    defaultMarkup: 25,
    roles: [
      { role: 'Project Manager', baseRate: 1200 },
      { role: 'Business Analyst', baseRate: 1000 },
      { role: 'Solution Architect', baseRate: 1500 },
      { role: 'Developer', baseRate: 900 },
      { role: 'QA Engineer', baseRate: 800 },
      { role: 'UX Designer', baseRate: 950 }
    ]
  });
  
  const [templates, setTemplates] = useState([]);
  
  const [conversionRules, setConversionRules] = useState({
    mapPhases: true,
    mapMilestones: true,
    mapRisks: true,
    mapDependencies: true,
    mapKPIs: true,
    mapAcceptance: true,
    createDefaultEpics: true,
    notifyStakeholders: false
  });
  
  const [aipmSettings, setAipmSettings] = useState({
    defaultActivation: 'on', // 'on', 'off', 'restricted'
    allowedRoles: ['pm', 'delivery_lead', 'consulting_admin', 'sysadmin'],
    requireConfirmation: true,
    prohibitAutoPlanChanges: true,
    logAllSuggestions: true,
    alertThreshold: 'medium', // 'low', 'medium', 'high'
    dailySummaryEnabled: true,
    weeklySummaryEnabled: true
  });
  
  const [usageStats, setUsageStats] = useState({
    totalProposals: 0,
    planningStepsCompleted: 0,
    projectsConverted: 0,
    templatesDownloaded: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load consulting suite settings from backend
      const [settingsRes, statsRes] = await Promise.all([
        api.getConsultingSuiteSettings().catch(() => ({ data: null })),
        api.getConsultingStats().catch(() => ({ data: null }))
      ]);
      
      if (settingsRes.data) {
        if (settingsRes.data.proposalSettings) setProposalSettings(settingsRes.data.proposalSettings);
        if (settingsRes.data.planningDefaults) setPlanningDefaults(settingsRes.data.planningDefaults);
        if (settingsRes.data.rateCardDefaults) setRateCardDefaults(settingsRes.data.rateCardDefaults);
        if (settingsRes.data.conversionRules) setConversionRules(settingsRes.data.conversionRules);
        if (settingsRes.data.templates) setTemplates(settingsRes.data.templates);
      }
      
      if (statsRes.data) {
        setUsageStats(statsRes.data);
      }
    } catch (error) {
      console.error('Failed to load consulting suite settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.saveConsultingSuiteSettings({
        proposalSettings,
        planningDefaults,
        rateCardDefaults,
        conversionRules,
        aipmSettings
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'proposal-settings', label: 'Proposal Builder', icon: FileText },
    { id: 'planning-defaults', label: 'Planning Defaults', icon: Calendar },
    { id: 'rate-card', label: 'Rate Card', icon: DollarSign },
    { id: 'templates', label: 'Templates Pack', icon: Download },
    { id: 'conversion', label: 'Conversion Rules', icon: ArrowRightCircle },
    { id: 'aipm', label: 'Agentic PM', icon: Brain }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
            <Briefcase className="w-7 h-7 text-purple-600" />
            Consulting Suite Administration
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">
            Configure Proposal Builder, Planning defaults, Rate Cards, and Templates
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          data-testid="save-consulting-settings"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : 'Save All Settings'}
        </button>
      </div>

      {/* Usage Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-2 text-purple-600 mb-2">
            <FileText className="w-5 h-5" />
            <span className="text-sm font-medium">Proposals</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{usageStats.totalProposals}</p>
          <p className="text-xs text-gray-500">Total created</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-2 text-blue-600 mb-2">
            <Calendar className="w-5 h-5" />
            <span className="text-sm font-medium">Planning Steps</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{usageStats.planningStepsCompleted}</p>
          <p className="text-xs text-gray-500">Completed</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-2 text-green-600 mb-2">
            <ArrowRightCircle className="w-5 h-5" />
            <span className="text-sm font-medium">Conversions</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{usageStats.projectsConverted}</p>
          <p className="text-xs text-gray-500">To projects</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-2 text-orange-600 mb-2">
            <Download className="w-5 h-5" />
            <span className="text-sm font-medium">Downloads</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{usageStats.templatesDownloaded}</p>
          <p className="text-xs text-gray-500">Templates</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex gap-1">
          {tabs.map((tab) => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <TabIcon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg border shadow-sm">
        {/* Proposal Builder Settings */}
        {activeTab === 'proposal-settings' && (
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between pb-4 border-b">
              <div>
                <h3 className="font-semibold text-gray-900">Proposal Builder Settings</h3>
                <p className="text-sm text-gray-500">Configure the proposal creation workflow</p>
              </div>
              <label className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Enable Proposal Builder</span>
                <input
                  type="checkbox"
                  checked={proposalSettings.enabled}
                  onChange={(e) => setProposalSettings({ ...proposalSettings, enabled: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  data-testid="proposal-enabled-toggle"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Proposal Number Prefix</label>
                <input
                  type="text"
                  value={proposalSettings.proposalPrefix}
                  onChange={(e) => setProposalSettings({ ...proposalSettings, proposalPrefix: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="PROP"
                />
                <p className="text-xs text-gray-500 mt-1">e.g., PROP-2026-001</p>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={proposalSettings.autoNumbering}
                    onChange={(e) => setProposalSettings({ ...proposalSettings, autoNumbering: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-purple-600"
                  />
                  <span className="text-sm text-gray-700">Auto-number proposals</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={proposalSettings.requireApproval}
                    onChange={(e) => setProposalSettings({ ...proposalSettings, requireApproval: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-purple-600"
                  />
                  <span className="text-sm text-gray-700">Require approval before export</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Proposal Sections</label>
              <div className="flex flex-wrap gap-2">
                {['discovery', 'scope', 'acceptance', 'planning', 'estimation', 'proposal'].map((section) => (
                  <label key={section} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border">
                    <input
                      type="checkbox"
                      checked={proposalSettings.defaultSections.includes(section)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setProposalSettings({
                            ...proposalSettings,
                            defaultSections: [...proposalSettings.defaultSections, section]
                          });
                        } else {
                          setProposalSettings({
                            ...proposalSettings,
                            defaultSections: proposalSettings.defaultSections.filter(s => s !== section)
                          });
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600"
                    />
                    <span className="text-sm capitalize">{section}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Planning Defaults */}
        {activeTab === 'planning-defaults' && (
          <div className="p-6 space-y-6">
            <div className="pb-4 border-b">
              <h3 className="font-semibold text-gray-900">Planning Defaults</h3>
              <p className="text-sm text-gray-500">Set default phases, milestones, and governance for new proposals</p>
            </div>

            {/* Default Phases */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  Default Phases
                </label>
                <button
                  onClick={() => setPlanningDefaults({
                    ...planningDefaults,
                    defaultPhases: [...planningDefaults.defaultPhases, { name: 'New Phase', durationWeeks: 2, deliverables: [] }]
                  })}
                  className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Add Phase
                </button>
              </div>
              <div className="space-y-2">
                {planningDefaults.defaultPhases.map((phase, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="w-6 h-6 bg-purple-100 text-purple-700 rounded text-xs flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <input
                      type="text"
                      value={phase.name}
                      onChange={(e) => {
                        const updated = [...planningDefaults.defaultPhases];
                        updated[idx].name = e.target.value;
                        setPlanningDefaults({ ...planningDefaults, defaultPhases: updated });
                      }}
                      className="flex-1 px-2 py-1 text-sm border rounded"
                    />
                    <input
                      type="number"
                      value={phase.durationWeeks}
                      onChange={(e) => {
                        const updated = [...planningDefaults.defaultPhases];
                        updated[idx].durationWeeks = parseInt(e.target.value) || 1;
                        setPlanningDefaults({ ...planningDefaults, defaultPhases: updated });
                      }}
                      className="w-16 px-2 py-1 text-sm border rounded text-center"
                      min={1}
                    />
                    <span className="text-xs text-gray-500">weeks</span>
                    <button
                      onClick={() => {
                        const updated = planningDefaults.defaultPhases.filter((_, i) => i !== idx);
                        setPlanningDefaults({ ...planningDefaults, defaultPhases: updated });
                      }}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Default Milestones */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Flag className="w-4 h-4" />
                  Default Milestones
                </label>
                <button
                  onClick={() => setPlanningDefaults({
                    ...planningDefaults,
                    defaultMilestones: [...planningDefaults.defaultMilestones, { name: 'New Milestone', week: 1 }]
                  })}
                  className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Add Milestone
                </button>
              </div>
              <div className="space-y-2">
                {planningDefaults.defaultMilestones.map((ms, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Flag className="w-4 h-4 text-green-600" />
                    <input
                      type="text"
                      value={ms.name}
                      onChange={(e) => {
                        const updated = [...planningDefaults.defaultMilestones];
                        updated[idx].name = e.target.value;
                        setPlanningDefaults({ ...planningDefaults, defaultMilestones: updated });
                      }}
                      className="flex-1 px-2 py-1 text-sm border rounded"
                    />
                    <span className="text-xs text-gray-500">Week</span>
                    <input
                      type="number"
                      value={ms.week}
                      onChange={(e) => {
                        const updated = [...planningDefaults.defaultMilestones];
                        updated[idx].week = parseInt(e.target.value) || 1;
                        setPlanningDefaults({ ...planningDefaults, defaultMilestones: updated });
                      }}
                      className="w-16 px-2 py-1 text-sm border rounded text-center"
                      min={1}
                    />
                    <button
                      onClick={() => {
                        const updated = planningDefaults.defaultMilestones.filter((_, i) => i !== idx);
                        setPlanningDefaults({ ...planningDefaults, defaultMilestones: updated });
                      }}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Default Governance */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Default Governance Cadence
                </label>
                <button
                  onClick={() => setPlanningDefaults({
                    ...planningDefaults,
                    defaultGovernance: [...planningDefaults.defaultGovernance, { name: 'New Meeting', type: 'weekly' }]
                  })}
                  className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Add Cadence
                </button>
              </div>
              <div className="space-y-2">
                {planningDefaults.defaultGovernance.map((gov, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <input
                      type="text"
                      value={gov.name}
                      onChange={(e) => {
                        const updated = [...planningDefaults.defaultGovernance];
                        updated[idx].name = e.target.value;
                        setPlanningDefaults({ ...planningDefaults, defaultGovernance: updated });
                      }}
                      className="flex-1 px-2 py-1 text-sm border rounded"
                    />
                    <select
                      value={gov.type}
                      onChange={(e) => {
                        const updated = [...planningDefaults.defaultGovernance];
                        updated[idx].type = e.target.value;
                        setPlanningDefaults({ ...planningDefaults, defaultGovernance: updated });
                      }}
                      className="px-2 py-1 text-sm border rounded"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Bi-weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="milestone">Per Milestone</option>
                    </select>
                    <button
                      onClick={() => {
                        const updated = planningDefaults.defaultGovernance.filter((_, i) => i !== idx);
                        setPlanningDefaults({ ...planningDefaults, defaultGovernance: updated });
                      }}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Default Roles */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Default Team Roles
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                {planningDefaults.defaultRoles.map((role, idx) => (
                  <span key={idx} className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm">
                    {role}
                    <button
                      onClick={() => {
                        const updated = planningDefaults.defaultRoles.filter((_, i) => i !== idx);
                        setPlanningDefaults({ ...planningDefaults, defaultRoles: updated });
                      }}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  placeholder="+ Add role"
                  className="px-3 py-1.5 text-sm border border-dashed rounded-lg"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      setPlanningDefaults({
                        ...planningDefaults,
                        defaultRoles: [...planningDefaults.defaultRoles, e.target.value.trim()]
                      });
                      e.target.value = '';
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Rate Card */}
        {activeTab === 'rate-card' && (
          <div className="p-6 space-y-6">
            <div className="pb-4 border-b">
              <h3 className="font-semibold text-gray-900">Global Rate Card Defaults</h3>
              <p className="text-sm text-gray-500">Set default rates and markup for new tenants</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Default Currency</label>
                <select
                  value={rateCardDefaults.defaultCurrency}
                  onChange={(e) => setRateCardDefaults({ ...rateCardDefaults, defaultCurrency: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="ZAR">ZAR - South African Rand</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="AUD">AUD - Australian Dollar</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Default Markup (%)</label>
                <input
                  type="number"
                  value={rateCardDefaults.defaultMarkup}
                  onChange={(e) => setRateCardDefaults({ ...rateCardDefaults, defaultMarkup: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  min={0}
                  max={100}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700">Default Role Rates</label>
                <button
                  onClick={() => setRateCardDefaults({
                    ...rateCardDefaults,
                    roles: [...rateCardDefaults.roles, { role: 'New Role', baseRate: 1000 }]
                  })}
                  className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Add Role
                </button>
              </div>
              <div className="space-y-2">
                {rateCardDefaults.roles.map((role, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    <input
                      type="text"
                      value={role.role}
                      onChange={(e) => {
                        const updated = [...rateCardDefaults.roles];
                        updated[idx].role = e.target.value;
                        setRateCardDefaults({ ...rateCardDefaults, roles: updated });
                      }}
                      className="flex-1 px-2 py-1 text-sm border rounded"
                    />
                    <span className="text-xs text-gray-500">{rateCardDefaults.defaultCurrency}</span>
                    <input
                      type="number"
                      value={role.baseRate}
                      onChange={(e) => {
                        const updated = [...rateCardDefaults.roles];
                        updated[idx].baseRate = parseInt(e.target.value) || 0;
                        setRateCardDefaults({ ...rateCardDefaults, roles: updated });
                      }}
                      className="w-24 px-2 py-1 text-sm border rounded text-right"
                    />
                    <span className="text-xs text-gray-500">/day</span>
                    <button
                      onClick={() => {
                        const updated = rateCardDefaults.roles.filter((_, i) => i !== idx);
                        setRateCardDefaults({ ...rateCardDefaults, roles: updated });
                      }}
                      className="p-1 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                <strong>Note:</strong> Individual tenants can override these defaults in their own Rate Card settings.
                Navigate to <button onClick={() => navigate('/consulting/rate-card')} className="underline">Rate Card Management</button> to view tenant-specific rates.
              </p>
            </div>
          </div>
        )}

        {/* Templates Pack */}
        {activeTab === 'templates' && (
          <div className="p-6 space-y-6">
            <div className="pb-4 border-b">
              <h3 className="font-semibold text-gray-900">Templates Pack Manager</h3>
              <p className="text-sm text-gray-500">Manage downloadable proposal templates</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { name: 'Proposal Intake Form', type: 'DOCX', size: '38.4 KB', path: '/downloads/scopeops/templates/proposal-intake-form.docx' },
                { name: 'Discovery Interview Script', type: 'PDF', size: '6.2 KB', path: '/downloads/scopeops/templates/discovery-interview-script.pdf' },
                { name: 'Requirements Starter', type: 'XLSX', size: '6.3 KB', path: '/downloads/scopeops/templates/requirements-acceptance-starter.xlsx' }
              ].map((template, idx) => (
                <div key={idx} className="p-4 border rounded-lg bg-gray-50">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-purple-600" />
                      <span className="font-medium text-gray-900">{template.name}</span>
                    </div>
                    <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">{template.type}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">Size: {template.size}</p>
                  <div className="flex gap-2">
                    <button className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs bg-white border rounded hover:bg-gray-50">
                      <Eye className="w-3 h-3" />
                      Preview
                    </button>
                    <button className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs bg-purple-600 text-white rounded hover:bg-purple-700">
                      <Upload className="w-3 h-3" />
                      Replace
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center">
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Drag and drop templates here or click to upload</p>
              <p className="text-xs text-gray-400 mt-1">Supports DOCX, PDF, XLSX (max 10MB)</p>
              <button className="mt-3 px-4 py-2 text-sm text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50">
                Upload New Template
              </button>
            </div>
          </div>
        )}

        {/* Conversion Rules */}
        {activeTab === 'conversion' && (
          <div className="p-6 space-y-6">
            <div className="pb-4 border-b">
              <h3 className="font-semibold text-gray-900">Proposal to Project Conversion Rules</h3>
              <p className="text-sm text-gray-500">Configure how proposals are converted to execution projects</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'mapPhases', label: 'Map Phases to Project Timeline', icon: Layers },
                { key: 'mapMilestones', label: 'Create Project Milestones', icon: Flag },
                { key: 'mapRisks', label: 'Transfer Risks to Project', icon: AlertTriangle },
                { key: 'mapDependencies', label: 'Copy Dependencies', icon: GitBranch },
                { key: 'mapKPIs', label: 'Assign KPIs to Project', icon: Settings },
                { key: 'mapAcceptance', label: 'Copy Acceptance Criteria', icon: Check },
                { key: 'createDefaultEpics', label: 'Create Epics from Scope', icon: Layers },
                { key: 'notifyStakeholders', label: 'Notify Stakeholders on Conversion', icon: Users }
              ].map((rule) => {
                const RuleIcon = rule.icon;
                return (
                  <label
                    key={rule.key}
                    className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border cursor-pointer hover:border-purple-300"
                  >
                    <input
                      type="checkbox"
                      checked={conversionRules[rule.key]}
                      onChange={(e) => setConversionRules({ ...conversionRules, [rule.key]: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <RuleIcon className="w-5 h-5 text-gray-500" />
                    <span className="text-sm text-gray-700">{rule.label}</span>
                  </label>
                );
              })}
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Conversion Process</h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>User clicks "Convert to Project" on approved proposal</li>
                <li>System creates new project with proposal metadata</li>
                <li>Scope tree (epics/features) is created from proposal scope</li>
                <li>Selected elements are mapped according to rules above</li>
                <li>User is redirected to new project workspace</li>
              </ol>
            </div>
          </div>
        )}

        {/* Agentic PM Settings */}
        {activeTab === 'aipm' && (
          <div className="p-6 space-y-6">
            <div className="pb-4 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-lg flex items-center justify-center">
                  <Brain className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Agentic PM Settings</h3>
                  <p className="text-sm text-gray-500">Configure AI-powered project management assistant</p>
                </div>
              </div>
            </div>

            {/* Default Activation */}
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-3">Default Activation</label>
              <div className="flex gap-3">
                {[
                  { value: 'on', label: 'On', desc: 'Enabled by default for new projects' },
                  { value: 'off', label: 'Off', desc: 'Disabled by default, must be manually enabled' },
                  { value: 'restricted', label: 'Restricted', desc: 'Only allowed roles can enable' }
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex-1 p-4 border rounded-lg cursor-pointer transition-colors ${
                      aipmSettings.defaultActivation === opt.value
                        ? 'bg-purple-50 border-purple-300'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="defaultActivation"
                        value={opt.value}
                        checked={aipmSettings.defaultActivation === opt.value}
                        onChange={(e) => setAipmSettings({ ...aipmSettings, defaultActivation: e.target.value })}
                        className="w-4 h-4 text-purple-600"
                      />
                      <span className="font-medium text-gray-900">{opt.label}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 ml-6">{opt.desc}</p>
                  </label>
                ))}
              </div>
            </div>

            {/* Allowed Roles */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Allowed Enabling Roles</label>
              <p className="text-xs text-gray-500 mb-3">Select which roles can enable/disable AI PM for projects</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'sysadmin', label: 'System Admin' },
                  { key: 'consulting_admin', label: 'Consulting Suite Admin' },
                  { key: 'pm', label: 'Project Manager' },
                  { key: 'delivery_lead', label: 'Delivery Lead' },
                  { key: 'engagement_manager', label: 'Engagement Manager' },
                  { key: 'pmo_power', label: 'PMO Power User' }
                ].map((role) => (
                  <label
                    key={role.key}
                    className={`flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer ${
                      aipmSettings.allowedRoles.includes(role.key)
                        ? 'bg-purple-50 border-purple-300'
                        : 'bg-white border-gray-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={aipmSettings.allowedRoles.includes(role.key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAipmSettings({ ...aipmSettings, allowedRoles: [...aipmSettings.allowedRoles, role.key] });
                        } else {
                          setAipmSettings({ ...aipmSettings, allowedRoles: aipmSettings.allowedRoles.filter(r => r !== role.key) });
                        }
                      }}
                      className="w-4 h-4 rounded text-purple-600"
                    />
                    <span className="text-sm">{role.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Safety Controls */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-5 h-5 text-amber-600" />
                <h4 className="font-medium text-amber-900">Safety Controls</h4>
              </div>
              <div className="space-y-3">
                <label className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Require confirmation for all actions</span>
                    <p className="text-xs text-gray-500">AI suggestions must be approved before execution</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={aipmSettings.requireConfirmation}
                    onChange={(e) => setAipmSettings({ ...aipmSettings, requireConfirmation: e.target.checked })}
                    className="w-5 h-5 rounded text-purple-600"
                  />
                </label>
                <label className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Prohibit automatic plan changes</span>
                    <p className="text-xs text-gray-500">AI cannot modify scope, timeline, or governance</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={aipmSettings.prohibitAutoPlanChanges}
                    disabled
                    className="w-5 h-5 rounded text-purple-600 bg-gray-100"
                  />
                  <span className="text-xs text-amber-600 font-medium ml-2">Locked</span>
                </label>
                <label className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Log all suggestions</span>
                    <p className="text-xs text-gray-500">Record all AI suggestions in audit log</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={aipmSettings.logAllSuggestions}
                    onChange={(e) => setAipmSettings({ ...aipmSettings, logAllSuggestions: e.target.checked })}
                    className="w-5 h-5 rounded text-purple-600"
                  />
                </label>
              </div>
            </div>

            {/* Reporting Options */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Automated Reports</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="flex items-center justify-between p-4 bg-gray-50 border rounded-lg">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Daily Summary</span>
                    <p className="text-xs text-gray-500">Auto-generate daily project summary</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={aipmSettings.dailySummaryEnabled}
                    onChange={(e) => setAipmSettings({ ...aipmSettings, dailySummaryEnabled: e.target.checked })}
                    className="w-5 h-5 rounded text-purple-600"
                  />
                </label>
                <label className="flex items-center justify-between p-4 bg-gray-50 border rounded-lg">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Weekly Status Draft</span>
                    <p className="text-xs text-gray-500">Auto-generate weekly status report</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={aipmSettings.weeklySummaryEnabled}
                    onChange={(e) => setAipmSettings({ ...aipmSettings, weeklySummaryEnabled: e.target.checked })}
                    className="w-5 h-5 rounded text-purple-600"
                  />
                </label>
              </div>
            </div>

            {/* Alert Threshold */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Alert Threshold</label>
              <p className="text-xs text-gray-500 mb-3">Sensitivity level for AI alerts and suggestions</p>
              <div className="flex gap-3">
                {[
                  { value: 'low', label: 'Low', desc: 'Only critical alerts' },
                  { value: 'medium', label: 'Medium', desc: 'Balanced alerts' },
                  { value: 'high', label: 'High', desc: 'All potential issues' }
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex-1 p-3 border rounded-lg cursor-pointer text-center ${
                      aipmSettings.alertThreshold === opt.value
                        ? 'bg-purple-50 border-purple-300'
                        : 'bg-white border-gray-200'
                    }`}
                  >
                    <input
                      type="radio"
                      name="alertThreshold"
                      value={opt.value}
                      checked={aipmSettings.alertThreshold === opt.value}
                      onChange={(e) => setAipmSettings({ ...aipmSettings, alertThreshold: e.target.value })}
                      className="sr-only"
                    />
                    <span className="font-medium text-gray-900">{opt.label}</span>
                    <p className="text-xs text-gray-500 mt-1">{opt.desc}</p>
                  </label>
                ))}
              </div>
            </div>

            {/* Info Box */}
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <h4 className="font-medium text-purple-900 mb-2">How Agentic PM Works</h4>
              <ul className="text-sm text-purple-800 space-y-1">
                <li>• Monitors project health, risks, KPIs, and milestones</li>
                <li>• Generates summaries and status report drafts</li>
                <li>• Suggests actions based on project state (requires confirmation)</li>
                <li>• Never modifies scope, plan, or governance automatically</li>
                <li>• All suggestions are logged for audit purposes</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsultingSuiteAdminPage;
